import * as i1 from '@angular/common';
import { CommonModule, NgClass } from '@angular/common';
import * as i0 from '@angular/core';
import { Input, ViewEncapsulation, Component, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { ActionBaseDirective } from '@windmill/ng-windmill/shared';

/**
 * To use this component in a module, you can import the `CentricButtonModule`:
 * @example
 * ```typescript
 * import { CentricButtonModule } from '@windmill/ng-windmill/button';
 * ```
 *
 * To use the standalone component directly, you can import `ButtonComponent`:
 * @example
 * ```typescript
 * import { ButtonComponent } from '@windmill/ng-windmill/button';
 * ```
 */
class ButtonComponent extends ActionBaseDirective {
    /**
     * Property that sets the native HTML type attribute
     */
    buttonType = 'submit';
    /**
     * Button data-testid
     */
    testButtonId;
    /**
     * If true, the button will be disabled
     */
    disabled = false;
    /**
     * Aria-haspopup for button
     */
    ariaHasPopup;
    /**
     * Aria-controls for button
     */
    ariaControls;
    /**
     * Aria-expanded for button
     */
    ariaExpanded;
    onClick(event) {
        if (this.disabled) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ButtonComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: ButtonComponent, isStandalone: true, selector: "centric-button", inputs: { buttonType: "buttonType", testButtonId: "testButtonId", disabled: "disabled", ariaHasPopup: "ariaHasPopup", ariaControls: "ariaControls", ariaExpanded: "ariaExpanded" }, usesInheritance: true, ngImport: i0, template: "<button\r\n  class=\"centric-inner-button\"\r\n  [ngClass]=\"buttonClass\"\r\n  [class.has-icon]=\"iconId\"\r\n  [disabled]=\"disabled\"\r\n  [attr.type]=\"buttonType\"\r\n  [attr.tabindex]=\"tabIndex\"\r\n  [attr.data-testid]=\"testButtonId\"\r\n  [attr.aria-label]=\"ariaLabel\"\r\n  [attr.aria-labelledby]=\"ariaLabelledby\"\r\n  [attr.aria-description]=\"ariaDescription\"\r\n  [attr.aria-describedby]=\"ariaDescribedby\"\r\n  [attr.aria-haspopup]=\"ariaHasPopup\"\r\n  [attr.aria-controls]=\"ariaControls\"\r\n  [attr.aria-expanded]=\"ariaExpanded\">\r\n  @if (iconId) {\r\n    <centric-icon2\r\n      [svgIcon]=\"iconId\"\r\n      [size]=\"determineIconSize\"\r\n      (click)=\"onClick($event)\">\r\n    </centric-icon2>\r\n  }\r\n  <span\r\n    class=\"button-content\"\r\n    (click)=\"onClick($event)\">\r\n    <ng-content></ng-content>\r\n  </span>\r\n</button>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-button', encapsulation: ViewEncapsulation.None, imports: [CommonModule, FormsModule, NgClass, CentricIcon2Component], standalone: true, template: "<button\r\n  class=\"centric-inner-button\"\r\n  [ngClass]=\"buttonClass\"\r\n  [class.has-icon]=\"iconId\"\r\n  [disabled]=\"disabled\"\r\n  [attr.type]=\"buttonType\"\r\n  [attr.tabindex]=\"tabIndex\"\r\n  [attr.data-testid]=\"testButtonId\"\r\n  [attr.aria-label]=\"ariaLabel\"\r\n  [attr.aria-labelledby]=\"ariaLabelledby\"\r\n  [attr.aria-description]=\"ariaDescription\"\r\n  [attr.aria-describedby]=\"ariaDescribedby\"\r\n  [attr.aria-haspopup]=\"ariaHasPopup\"\r\n  [attr.aria-controls]=\"ariaControls\"\r\n  [attr.aria-expanded]=\"ariaExpanded\">\r\n  @if (iconId) {\r\n    <centric-icon2\r\n      [svgIcon]=\"iconId\"\r\n      [size]=\"determineIconSize\"\r\n      (click)=\"onClick($event)\">\r\n    </centric-icon2>\r\n  }\r\n  <span\r\n    class=\"button-content\"\r\n    (click)=\"onClick($event)\">\r\n    <ng-content></ng-content>\r\n  </span>\r\n</button>\r\n" }]
        }], propDecorators: { buttonType: [{
                type: Input
            }], testButtonId: [{
                type: Input
            }], disabled: [{
                type: Input
            }], ariaHasPopup: [{
                type: Input
            }], ariaControls: [{
                type: Input
            }], ariaExpanded: [{
                type: Input
            }] } });

const moduleComponents = [ButtonComponent];
class CentricButtonModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricButtonModule, imports: [CommonModule, FormsModule, CentricIcon2Module, ButtonComponent], exports: [ButtonComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricButtonModule, imports: [CommonModule, FormsModule, CentricIcon2Module, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, CentricIcon2Module, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ButtonComponent, CentricButtonModule };
